import { BrowserRouter, Navigate, Route, Routes } from "react-router";

import { SiteLayout } from "@/components/layout/SiteLayout";
import { detectLocale } from "@/i18n";
import {
  ContactPage,
  FeaturesPage,
  HowItWorksPage,
  NotFoundPage,
  PricingPage,
  ProcessingModesPage,
  SecurityPage,
} from "@/pages/ContentPages";
import { Landing } from "@/pages/Landing";
import { PrivacyPage, TermsPage } from "@/pages/LegalPages";

/**
 * Routing.
 *
 * Every page lives under an explicit locale segment (`/ar/...`, `/en/...`) so
 * the language is shareable, linkable and indexable rather than hidden in
 * client state. The bare root redirects to the visitor's best-guess locale,
 * defaulting to Arabic.
 *
 * ## There is no `/login` or `/signup` route here
 *
 * Both belong to the console, which owns the product's only authentication
 * surface. They are also not this site's to serve: the production vhost matches
 * `^/(ar|en)/(start|login|signup|pricing|account|download|contact)` and hands
 * those paths to the console SPA through deployment routing, so a route
 * declared here would render on the dev server and nowhere else — which is the
 * worst place for a difference to live.
 *
 * Sign-in links are therefore plain anchors built by `lib/platform-url.ts`.
 */
export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Navigate to={`/${detectLocale()}`} replace />} />

        <Route path="/:locale" element={<SiteLayout />}>
          <Route index element={<Landing />} />
          <Route path="features" element={<FeaturesPage />} />
          <Route path="how-it-works" element={<HowItWorksPage />} />
          <Route path="processing-modes" element={<ProcessingModesPage />} />
          <Route path="security" element={<SecurityPage />} />
          <Route path="pricing" element={<PricingPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* Anything outside a known locale falls back to the default one. */}
        <Route path="*" element={<Navigate to={`/${detectLocale()}`} replace />} />
      </Routes>
    </BrowserRouter>
  );
}
